/**
 * gets a value by path using . notation
 * @param  {json object} obj json object
 * @param  {string} key dot notation path in object
 * @return {undefined} if no result or the value at th epath.
 * example
 *	 const a = {
 *		"people":
 *			"name": "seth"
 *		}
 *	 }
 *	 getObjValue(a, "people.name") // "seth"
 *	 getObjValue(a, "people.lname") // undefined
 */
export function getObjValue(obj, key) {
	return key.split('.').reduce((o, x) => {
		return (typeof o === 'undefined' || o === null) ? o : o[x];
	}, obj);
}

export function isJSON(jsonString) {
	try {
		JSON.parse(jsonString);
	} catch (e) {
		return false;
	}
	return true;
}

export function isArray(arrayString) {
	if (!isJSON(arrayString)) {
		return false;
	} else {
		const object = JSON.parse(arrayString);
		return Array.isArray(object);
	}
}

export function isObject(objectString) {
	if (!isJSON(objectString)) {
		return false;
	} else {
		if (objectString.indexOf('{') < 0) {
			return false;
		}
		const object = JSON.parse(objectString);
		return !isArray(object);
	}
}

export function isBool(boolString) {
	switch (boolString) {
		case 'false':
		case 'true':
			return true;
		default:
			return false;
	}
}

export function isNumber(numberString) {
	if (numberString === '') {
		return false;
	}
	return !isNaN(numberString);
}

export function trim(string) {
	if (typeof (string) !== 'string') {
		return string;
	}
	return string.replace('\\t', '').trim();
}

/**
 * Converts supported object types to string.
 */
export function objectTypeToString(type) {
	if (!type) {
		return '';
	}
	switch (type) {
		case Array:
			return 'array';
		case Boolean:
			return 'boolean';
		case Date:
			return 'date';
		case Number:
			return 'number';
		case Object:
			return 'object';
		case String:
			return 'string';
		default:
			if (typeof (type) === 'string') {
				return type.toLowerCase();
			} else {
				return `${type}`;
			}
	}
}

export function stringTypeToObject(type) {
	switch (type.toLowerCase()) {
		case 'array':
			return Array;
		case 'boolean':
			return Boolean;
		case 'date':
			return Date;
		case 'number':
			return Number;
		case 'object':
			return Object;
		case 'string':
			return String;
		default:
			return `${type}`;
	}
}

export function castType(input) {
	function castArray(arrayString) {
		if (!isJSON(arrayString)) {
			return false;
		} else {
			const object = JSON.parse(arrayString);
			if (Array.isArray(object)) {
				return object;
			}
		}
	}

	function castObject(objectString) {
		if (!isJSON(objectString)) {
			return false;
		} else {
			const object = JSON.parse(objectString);
			if (!Array.isArray(object)) {
				return object;
			} else {
				return false;
			}
		}
	}

	function castBool(boolString) {
		if (boolString === 'false' || boolString === 'true') {
			switch (boolString) {
				case 'false':
					return { is: true, value: false };
				case 'true':
					return { is: true, value: true };
			}
		}
		return false;
	}

	function castNumber(numberString) {
		if (!isNaN(numberString)) {
			return Number(numberString);
		}
		return false;
	}

	const arrayOut = castArray(input);
	if (arrayOut) {
		return arrayOut;
	}

	const boolOut = castBool(input);
	if (boolOut.is) {
		return boolOut.value;
	}

	const numberOut = castNumber(input);
	if (numberOut) {
		return numberOut;
	}

	const objectOut = castObject(input);
	if (objectOut) {
		return objectOut;
	}

	return input;
}

export function isLocalStorageAvailable() {
	const test = 'test';
	try {
		localStorage.setItem(test, test);
		localStorage.removeItem(test);
		return true;
	} catch (e) {
		return false;
	}
}

/** Copies a string to the clipboard. Must be called from within an
* event handler such as click. May return false if it failed, but
* this is not always possible. Browser support for Chrome 43+,
* Firefox 42+, Safari 10+, Edge and IE 10+.
* IE: The clipboard feature may be disabled by an administrator. By
* default a prompt is shown the first time the clipboard is
* used (per session).
* https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
*/
export function copyToClipboard(text) {
	if (!text) {
		return false;
	}
	/* eslint-disable */
	if (window.clipboardData && window.clipboardData.setData) {
		// IE specific code path to prevent textarea being shown while dialog is visible.
		return clipboardData.setData("Text", text);
	} else if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
		let textarea = document.createElement("textarea");
		textarea.setAttribute("id", "copyTextToClipboardElement")
		textarea.textContent = text;
		textarea.style.position = "fixed"; // Prevent scrolling to bottom of page in MS Edge.
		document.body.appendChild(textarea);
		document.getElementById("copyTextToClipboardElement").select();
		try {
			return document.execCommand("copy"); // Security exception may be thrown by some browsers.
		} catch (ex) {
			console.warn("Copy to clipboard failed.", ex);
			return false;
		} finally {
			let element = document.getElementById("copyTextToClipboardElement");
			element.parentNode.removeChild(element);
		}
	}
	/* eslint-enable */
}

/**
 * Generates example from provided schema.
 * @param {Object} schema - the schema to generate examples for.
 * @param {boolean} paramsOnly - true if the example should be generated
 * only for params section.
 * @returns {Object} the generated schema example.
 */
export function generateExampleFromSchema(schema, paramsOnly) {
	// Schema could lack properties when we create a Flow
	// from an Endpoint
	if (!schema || !schema.properties) {
		return {};
	}
	if (paramsOnly) {
		return generateExampleFromSchema(schema.properties.params);
	}
	const example = {};
	const props = Object.keys(schema.properties);
	for (const prop of props) {
		const param = schema.properties[prop];
		const paramType = param.type;
		let data;
		if (paramType === 'null') {
			data = null;
		} else if (paramType === 'array') {
			data = [];
		} else if (param.properties) {
			// This can be anything but we can try and iterate properties at
			// least maybe this will only be useful for generating GreetFlow
			// though but it's something.
			data = generateExampleFromSchema(param);
		} else if (paramType === 'object' || paramType === undefined) {
			// anything without a type is treated as an object
			// i.e. anyOf, oneOf, allOf, and not
			data = {};
		} else if (paramType === 'number' || paramType === 'integer') {
			data = 0;
		} else if (paramType === 'string') {
			data = '';
		} else if (paramType === 'boolean') {
			data = true;
		}
		example[prop] = data;
	}

	return example;
}
